import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import productService from '../utils/productService';
import { FaArrowLeft, FaBoxOpen, FaBoxes, FaPencilAlt, FaUsers } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const ProductDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);

  useEffect(() => {
    productService.getById(id)
      .then(setProduct)
      .catch(() => setProduct(null));
  }, [id]);

  if (!product) {
    return (
      <section className="col-span-1 md:col-span-10 p-4 md:py-6">
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-left shadow-sm">
          <h2 className="text-xl font-semibold text-slate-900">{t('dashboard.products.detailNotFound')}</h2>
          <p className="mt-2 text-sm text-slate-500">{t('dashboard.products.detailNotFoundNote')}</p>
          <button onClick={() => navigate('/dashboard/products')} className="mt-4 inline-flex items-center gap-2 rounded-md bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-200">
            <FaArrowLeft className="h-4 w-4" /> {t('dashboard.products.backToProducts')}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="col-span-1 md:col-span-10 p-4 md:py-6">
      <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-4 shadow-sm md:p-6">
        <div className="mb-6 flex flex-col justify-between gap-4 rounded-lg bg-white p-5 text-left shadow-sm md:flex-row md:items-center">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">{t('dashboard.products.detailLabel')}</p>
            <h2 className="mt-2 truncate text-3xl font-semibold text-[#00595A]">{product.name}</h2>
            <p className="mt-2 text-sm text-slate-500">{product.description || t('dashboard.products.detailSubtitle')}</p>
          </div>
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded bg-[#00595A] text-white shadow-sm">
            <FaBoxOpen className="h-6 w-6" />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="rounded-lg border border-slate-200 bg-white p-5 text-left shadow-sm lg:col-span-2 md:p-6">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-lg bg-emerald-50 p-4"><p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">{t('dashboard.products.stock')}</p><p className="mt-2 text-3xl font-semibold text-slate-900">{product.stock}</p></div>
              <div className="rounded-lg bg-amber-50 p-4"><p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-700">{t('dashboard.products.minimumStock')}</p><p className="mt-2 text-3xl font-semibold text-slate-900">{product.minimum_stock}</p></div>
              <div className="rounded-lg bg-sky-50 p-4"><p className="text-xs font-semibold uppercase tracking-[0.16em] text-sky-700">{t('dashboard.products.required')}</p><p className="mt-2 text-3xl font-semibold text-slate-900">{product.required_stock}</p></div>
              <div className="rounded-lg bg-teal-50 p-4"><p className="text-xs font-semibold uppercase tracking-[0.16em] text-teal-700">{t('dashboard.products.price')}</p><p className="mt-2 text-3xl font-semibold text-slate-900">${Number(product.price).toFixed(2)}</p></div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-5 text-left shadow-sm md:p-6">
            <h3 className="text-lg font-semibold text-slate-900">{t('dashboard.products.detailActions')}</h3>
            <p className="mt-2 text-sm text-slate-500">{t('dashboard.products.editSubtitle')}</p>
            <div className="mt-5 flex flex-col gap-3">
              <button onClick={() => navigate(`/dashboard/products/${product.id}/edit`)} className="inline-flex items-center justify-center gap-2 rounded-md bg-amber-100 px-4 py-3 text-sm font-semibold text-amber-800 transition hover:bg-amber-200"><FaPencilAlt className="h-4 w-4" /> {t('dashboard.common.edit')}</button>
              <button onClick={() => navigate(`/dashboard/products/${product.id}/stock`)} className="inline-flex items-center justify-center gap-2 rounded-md bg-emerald-100 px-4 py-3 text-sm font-semibold text-emerald-800 transition hover:bg-emerald-200"><FaBoxes className="h-4 w-4" /> {t('dashboard.products.addStock')}</button>
              <button onClick={() => navigate('/dashboard/products')} className="inline-flex items-center justify-center gap-2 rounded-md bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-200"><FaArrowLeft className="h-4 w-4" /> {t('dashboard.products.backToProducts')}</button>
            </div>
            <div className="mt-6 rounded-lg bg-slate-50 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><FaUsers className="h-4 w-4 text-slate-500" /> {t('dashboard.products.detailDemand')}</div>
              <p className="mt-2 text-sm text-slate-500">{t('dashboard.products.detailDemandNote')}</p>
              <p className="mt-3 text-sm text-slate-600">
                {t('dashboard.products.minimumStock')}: <span className="font-semibold text-slate-900">{product.minimum_stock}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductDetail;
